import type {Component, Editor} from 'grapesjs';
import {MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openErrorModal} from '@Modals/error-modal';
import {openCropperModal} from '@Components/modals/cropper-modal/cropper-modal.library';
import type {CropData, CropperPanelApplyResponse} from '@Panels/cropper-panel/cropper-panel.types';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS} from '@EmailMakerReact/email-maker-react.constants';
import {
  getAllComponentsInTree,
  getComponentByLoadIdentifier,
  getComponentMediaType,
  getStringProp,
  hasBackgroundImage,
} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {addComponentAnchor, EMBEDDED_VIDEO_ATTRIBUTE} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {updateMsOutlookStyles} from '@EmailMakerReact/lib/email-maker-compatibility-library';
import {canAddUniqueGettyAsset, releaseGettyAsset, reserveGettyAsset, showGettyLimitMessage, syncGettyCountForComponents} from '@EmailMakerReact/lib/email-maker-getty-library';
import {getCurrentIdEmailTemplate} from '@EmailMakerReact/lib/email-maker-storage-library';
import {getEmailMakerPollingEventId} from '@EmailMakerReact/lib/email-maker-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

/** One shared toast key for all concurrent preset creations, so overlapping loads collapse into a single toast. */
const PRESET_CREATION_LOADING_KEY = 'emailPresetCreation';
/** A crop equals the full image when every edge is within this tolerance. */
const MAX_CROP_THRESHOLD_PX = 1;

interface EmailMediaInfo {
  url: string;
  width: number;
  height: number;
}

interface MediaSrcRequest {
  hashedFilename: string;
  source: string;
  mediaType: string;
  removedBackgroundURL: string | undefined;
  showLoadingToast: boolean;
}

export interface SetImageLoadingOptions {
  component?: Component;
  loading: boolean;
  text?: string;
}

export interface SetImageSrcOptions {
  component: Component;
  /** One of the MULTI_MEDIA_SOURCE_* / upload source constants. */
  source: string;
  hashedFilename: string;
  /** Set the resolved URL as a background-image style instead of the src attribute. */
  isBackground?: boolean;
  backgroundRemovedURL?: string;
  /** Defaults to true. */
  showLoadingToast?: boolean;
  /** Runs with the resolved URL once the source has been applied. */
  callback?: (url: string) => void;
}

export interface SetEmbeddedVideoSrcOptions {
  /** Falls back to the current selection when omitted. */
  component?: Component;
  thumbnailURL: string;
  videoURL: string;
}

export interface EmailUploadImageResponse {
  src: string;
  ext: string;
  filename: string;
}

export interface ComponentWithUrlMeta {
  component: Component;
  url: string;
  source: string;
}

/** CropData rounding; the key order is serialized into saved templates and MUST be preserved. */
export function normalizeCropData(cropData: Partial<CropData>): CropData {
  return {
    x: Math.round(cropData.x ?? 0),
    y: Math.round(cropData.y ?? 0),
    width: Math.round(cropData.width ?? 0),
    height: Math.round(cropData.height ?? 0),
    imageWidth: Math.round(cropData.imageWidth ?? 0),
    imageHeight: Math.round(cropData.imageHeight ?? 0),
  };
}

export function isCropDataMaxCropped(cropData: CropData): boolean {
  const deltaWidth = Math.abs(cropData.width - cropData.imageWidth);
  const deltaHeight = Math.abs(cropData.height - cropData.imageHeight);

  return cropData.x <= MAX_CROP_THRESHOLD_PX && cropData.y <= MAX_CROP_THRESHOLD_PX && deltaWidth <= MAX_CROP_THRESHOLD_PX && deltaHeight <= MAX_CROP_THRESHOLD_PX;
}

/** Runs outside the undo history (editor.getModel().skip) so the loading class never reaches the save payload. */
export function setImageLoading(editor: Editor, options: SetImageLoadingOptions): void {
  editor.getModel().skip(() => {
    const {component} = options;
    if (!component) {
      return;
    }

    if (options.loading) {
      component.addClass('-loading');
      showLoading(`${component.cid}:img-loading`, {text: options.text});
    } else {
      component.removeClass('-loading');
      hideLoading(`${component.cid}:img-loading`);
    }
  });
}

export async function setImageSrc(editor: Editor, options: SetImageSrcOptions): Promise<void> {
  const {component, source, hashedFilename} = options;

  const isNewImage = component.get(EMAIL_MAKER_PROPS.HASHED_FILENAME) !== hashedFilename;

  if (source !== MULTI_MEDIA_SOURCE_GETTY || !isNewImage) {
    return applyImageSrc(editor, options, isNewImage);
  }

  const gettyMediaType = getComponentMediaType(editor, component);
  if (!canAddUniqueGettyAsset(editor, {mediaType: gettyMediaType, candidateHashedFilename: hashedFilename, excludeComponent: component})) {
    setImageLoading(editor, {component, loading: false});
    showGettyLimitMessage(gettyMediaType);
    return;
  }

  reserveGettyAsset(gettyMediaType, hashedFilename);

  try {
    await applyImageSrc(editor, options, isNewImage);
  } finally {
    releaseGettyAsset(gettyMediaType, hashedFilename);
  }
}

async function applyImageSrc(editor: Editor, options: SetImageSrcOptions, isNewImage: boolean): Promise<void> {
  const {component, source, hashedFilename, backgroundRemovedURL, showLoadingToast = true} = options;

  let cropData: CropData | undefined;
  if (isNewImage) {
    component.set(EMAIL_MAKER_PROPS.CROP_DATA, undefined);
    component.set(EMAIL_MAKER_PROPS.UNCROPPED_SRC, undefined);
    component.set(EMAIL_MAKER_PROPS.REMOVED_BACKGROUND_URL, undefined);
  } else {
    const storedCropData = component.get(EMAIL_MAKER_PROPS.CROP_DATA) as Partial<CropData> | undefined;
    cropData = storedCropData ? normalizeCropData(storedCropData) : undefined;
  }

  setImageLoading(editor, {
    component,
    loading: true,
    text: cropData ? window.i18next.t('pmwjs_cropping') : window.i18next.t('pmwjs_loading'),
  });

  const removedBackgroundURL = getStringProp(component, EMAIL_MAKER_PROPS.REMOVED_BACKGROUND_URL);
  const mediaType = getStringProp(component, EMAIL_MAKER_PROPS.MEDIA_TYPE) ?? '';

  let info: EmailMediaInfo;
  try {
    info = cropData
      ? await getCroppedSrcInfo(hashedFilename, cropData, mediaType, removedBackgroundURL)
      : await getMediaSrcInfo(editor, {hashedFilename, source, mediaType, removedBackgroundURL, showLoadingToast});
  } catch (error) {
    logEmailMakerError('setImageSrc: failed to resolve media source', error);
    openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
    setImageLoading(editor, {component, loading: false});
    return;
  }

  if (!info.url) {
    setImageLoading(editor, {component, loading: false});
    return;
  }

  if (options.isBackground) {
    setComponentStyle(editor, {component, property: 'background-image', value: `url(${info.url})`});
  } else {
    if (backgroundRemovedURL) {
      component.set(EMAIL_MAKER_PROPS.UNCROPPED_SRC, backgroundRemovedURL);
    }

    component.set('src', info.url);
    updateMsOutlookStyles(editor, component);
  }

  component.set(EMAIL_MAKER_PROPS.CONTENT_SOURCE, source);
  component.set(EMAIL_MAKER_PROPS.HASHED_FILENAME, hashedFilename);
  setImageLoading(editor, {component, loading: false});

  syncGettyCountForComponents(editor);
  options.callback?.(info.url);
}

export function setImageCropData(editor: Editor, component: Component | undefined, cropData: CropData | undefined): void {
  if (!cropData || !component?.get(EMAIL_MAKER_PROPS.IS_CROPPABLE)) {
    return;
  }

  const isFirstCrop = !component.get(EMAIL_MAKER_PROPS.CROP_DATA);
  if (isFirstCrop) {
    component.set(EMAIL_MAKER_PROPS.UNCROPPED_SRC, component.get('src'));
  }

  const normalizedCropData = normalizeCropData(cropData);
  component.set(EMAIL_MAKER_PROPS.CROP_DATA, isCropDataMaxCropped(normalizedCropData) ? undefined : normalizedCropData);

  void setImageSrc(editor, {
    component,
    source: getStringProp(component, EMAIL_MAKER_PROPS.CONTENT_SOURCE) ?? '',
    hashedFilename: getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '',
  });
}

/** Applies the embed thumbnail and wraps the component in a link to the video URL. */
export function setEmbeddedVideoSrc(editor: Editor, options: SetEmbeddedVideoSrcOptions): void {
  const component = options.component ?? editor.getSelected();
  if (!component) {
    return;
  }

  const styles = component.getStyle();
  styles['max-width'] = '100%';
  setImageLoading(editor, {component, loading: false});
  component.set('src', options.thumbnailURL);
  component.addAttributes({[EMBEDDED_VIDEO_ATTRIBUTE]: 1});
  component.setStyle(styles);

  addComponentAnchor(editor, {
    component,
    url: options.videoURL,
    shouldOpenInNewTab: true,
  });
}

export function updateLoadingImageWithResponse(editor: Editor, identifier: string, imageResponse: EmailUploadImageResponse): void {
  if (!identifier) {
    return;
  }

  const component = getComponentByLoadIdentifier(editor, identifier);
  if (!component) {
    return;
  }

  void setImageSrc(editor, {
    component,
    source: imageResponse.src,
    hashedFilename: imageResponse.filename,
  });
}

export async function getAllImagesInfo(editor: Editor): Promise<ComponentWithUrlMeta[]> {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper) {
    return [];
  }

  const mediaComponents = getAllComponentsInTree(wrapper).filter((component) => {
    return (
      component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE ||
      component.get('type') === EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO ||
      hasBackgroundImage(component)
    );
  });

  const results = await Promise.all(mediaComponents.map(getImageInfoForComponent));
  return results.filter((result): result is ComponentWithUrlMeta => result !== null);
}

/** Videos plus gif backgrounds on tables/sections/cells. */
export function getVideoCount(editor: Editor): number {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return 0;
  }

  const videos = wrapper.findType(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO);
  const tables = wrapper.findType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_TABLE);
  const sections = wrapper.findType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION);
  const cells = wrapper.findType(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL);
  const componentsWithVideoBackgrounds = [...tables, ...sections, ...cells].filter((component) => {
    const backgroundImage = component.getStyle()['background-image'];
    return typeof backgroundImage === 'string' && backgroundImage !== '' && /\.gif/i.test(backgroundImage);
  });

  return videos.length + componentsWithVideoBackgrounds.length;
}

/** Opens the React cropper modal for the component's (uncropped) source. */
export function openImageCropper(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  const attributes = component.getAttributes();
  const aspectRatio = Number(attributes.width) / Number(attributes.height) || 1;
  const initialCropData = component.get(EMAIL_MAKER_PROPS.CROP_DATA) as CropData | undefined;
  const contentSource = getStringProp(component, EMAIL_MAKER_PROPS.CONTENT_SOURCE);

  openCropperModal({
    initialCropData: initialCropData ?? null,
    showCancelButton: false,
    aspectRatio,
    showRemoveBackgroundOption: contentSource ? contentSource !== PMWStockImageSource.PMW_STOCK_IMAGE : undefined,
    getImgSrc: (): string => {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      return (component.get(EMAIL_MAKER_PROPS.UNCROPPED_SRC) || component.get('src')) as string;
    },
    onApply: (cropResponse: CropperPanelApplyResponse): void => {
      setImageCropData(editor, component, cropResponse.cropData);
    },
  });
}

/** Resolves the email-optimized media URL, creating presets through the polling pair on a 404. */
async function getMediaSrcInfo(editor: Editor, request: MediaSrcRequest): Promise<EmailMediaInfo> {
  try {
    const response = (await window.PMW.readLocal('emailmaker/getEmailMediaURL', {
      hashedFilename: request.hashedFilename,
      type: request.mediaType,
      removedBackgroundURL: request.removedBackgroundURL,
    })) as EmailMediaInfo;
    return {url: response.url, width: response.width, height: response.height};
  } catch (error) {
    if (getAjaxErrorCode(error) !== 404) {
      // Non-404 / transport failure: log and rethrow so the caller does not apply a blank URL.
      logEmailMakerError('getMediaSrcInfo: non-404 failure fetching email media URL', error);
      throw error;
    }

    // 404: the preset does not exist yet — create it through the polling pair.
    if (request.showLoadingToast) {
      showLoading(PRESET_CREATION_LOADING_KEY, {text: window.i18next.t('pmwjs_optimising_upload')});
    }

    try {
      const response = (await window.PMW.pollingAjaxCallAsync(
        async (): Promise<unknown> => {
          return createEmailMediaPresets(editor, request);
        },
        async (): Promise<unknown> => {
          return checkEmailMediaPresetsStatus(request.hashedFilename, request.mediaType);
        },
        undefined,
        getEmailMakerPollingEventId(editor)
      )) as EmailMediaInfo | undefined;

      // pollingAjaxCallAsync resolves to undefined on timeout rather than throwing.
      if (response) {
        return {url: response.url, width: response.width, height: response.height};
      }

      openErrorModal({message: window.i18next.t('pmwjs_error_try_again_reload_page')});
      return {url: '', width: 0, height: 0};
    } catch (presetError) {
      openErrorModal({message: getAjaxErrorMessage(presetError)});
      return {url: '', width: 0, height: 0};
    } finally {
      if (request.showLoadingToast) {
        hideLoading(PRESET_CREATION_LOADING_KEY);
      }
    }
  }
}

/** Persists the template first so the preset job can see the media reference. */
async function createEmailMediaPresets(editor: Editor, request: MediaSrcRequest): Promise<unknown> {
  if (!editor.StorageManager.isAutosave()) {
    await editor.store({});
  }

  return window.PMW.writeLocal('emailmaker/createEmailMediaPresets', {
    idEmail: getCurrentIdEmailTemplate(editor),
    source: request.source,
    hashedFilename: request.hashedFilename,
    type: request.mediaType,
  });
}

function checkEmailMediaPresetsStatus(hashedFilename: string, mediaType: string): Promise<unknown> {
  return window.PMW.readLocal('emailmaker/checkEmailMediaPresetStatus', {
    hashedFilename,
    type: mediaType,
  }) as Promise<unknown>;
}

/** Crop params are spread in a fixed key order matching saved templates. */
async function getCroppedSrcInfo(hashedFilename: string, cropData: CropData, mediaType: string, removedBackgroundURL: string | undefined): Promise<EmailMediaInfo> {
  try {
    return (await window.PMW.writeLocal('emailmaker/getCroppedMediaURL', {
      type: mediaType,
      hashedFilename,
      ...cropData,
      removedBackgroundURL,
    })) as EmailMediaInfo;
  } catch (error) {
    logEmailMakerError('getCroppedSrcInfo: failed to fetch cropped media URL', error);
    openErrorModal();
    return {url: '', width: 0, height: 0};
  }
}

async function getImageInfoForComponent(component: Component): Promise<ComponentWithUrlMeta | null> {
  try {
    const {url, source} = (await window.PMW.readLocal('emailmaker/getAssetImageInformation', {
      hashedFilename: getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME) ?? '',
      type: getStringProp(component, EMAIL_MAKER_PROPS.MEDIA_TYPE) ?? '',
    })) as {url: string; source: string};

    return {component, url, source};
  } catch (error) {
    const detail = getAjaxErrorDetail(error) ?? getAjaxErrorMessage(error);
    logEmailMakerError('getImageInfoForComponent: failed to fetch asset image information', detail);
    return null;
  }
}

/** Ajax errors thrown by readLocal/writeLocal are Errors with code/detail extras (see core.js getErrorAjaxResponse). Shared with `email-maker-background-removal-library.ts`. */
export function getAjaxErrorCode(error: unknown): string | number | null {
  if (error && typeof error === 'object' && 'code' in error) {
    const {code} = error as {code: unknown};
    if (typeof code === 'string' || typeof code === 'number') {
      return code;
    }
  }
  return null;
}

function getAjaxErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '';
}

function getAjaxErrorDetail(error: unknown): string | null {
  if (error && typeof error === 'object' && 'detail' in error && typeof error.detail === 'string' && error.detail !== '') {
    return error.detail;
  }
  return null;
}
